// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "../TestRunConstants";
import { IconButton, Link, useTheme } from "@material-ui/core";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, {
  ReactElement,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { format, isValid } from "date-fns";
import {
  setNewTestrunProfile,
  updateRegistration,
  useTestRunState,
} from "store/testrun";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Grid from "@material-ui/core/Grid/Grid";
import LoadingOverlay from "components/ui/LoadingOverlay";
import Paragraph from "components/ui/Paragraph";
import QuestionnaireForm from "../../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { RegistrationResult } from "SharedTypes";
import { capitalizeWords } from "components/ui/Questionnaire/QuestionnaireFormatters";
import { confirmAlert } from "../../../utils/confirmAlert";
import { fbt } from "fbt";
import { getFirebaseAppSync } from "components/Firebase/Firebase";
import { questionnaireDateValidator } from "components/ui/Questionnaire/validators/questionnaireDateValidator";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAppDispatch } from "store";
import { useAuthState } from "../../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";

export const capturedBarcode = {};

const RegistrationPanel = memo(() => {
  const { getNextDefaultStep, getNextStepOfType } = useTestRunConfig();
  const { step, testRunUID } = useParams<TestRunParams>();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const history = useHistory();
  const firebaseApp = getFirebaseAppSync();
  const { auth } = useAuthState();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const storedRegID = testRunState.testRun?.registration?.registrationID;

  const savedBarcode = testRunState.testRun?.registration?.barcode;
  const [storedBarcode, setStoredBarcode] = useState(
    capturedBarcode[testRunUID] || savedBarcode || ""
  );

  // If already recorded, enable "next" button
  useEffect(() => {
    delete capturedBarcode[testRunUID];
    if (savedBarcode && (storedBarcode === undefined || storedBarcode === "")) {
      setStoredBarcode(savedBarcode);
      return;
    }

    if (storedRegID && storedBarcode) {
      stepReadyCallback(true);
    }
  }, [storedBarcode, storedRegID, stepReadyCallback, testRunUID, savedBarcode]);

  // TODO: Generic Error handler.
  if (!auth) {
    return <div>Oops, something went wrong</div>;
  }

  if (!testRunState.subscribed) {
    return <div />; // Add Loader
  }

  const photoCaptureRoute = getNextStepOfType({
    currentStepName: step!,
    nextStepType: "captureBarcode",
  });

  let photoCaptureLink: ReactElement | null = null;

  if (photoCaptureRoute) {
    photoCaptureLink = (
      <Link
        component={RouterLink}
        style={{ marginRight: "-20px" }}
        to={getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
          params: {
            ":testRunUID": testRunUID,
            ":step": getNextStepOfType({
              currentStepName: step!,
              nextStepType: "captureBarcode",
            })!,
          },
        })}
      >
        <IconButton edge="end" color="inherit" aria-label="back" size="medium">
          <FontAwesomeIcon icon="camera" />
        </IconButton>
      </Link>
    );
  }

  return (
    <div>
      <LoadingOverlay isOpen={isLoading} title="Processing..." />
      <QuestionnaireFormMemoized
        formId={FORMID}
        onFormReady={stepReadyCallback}
        onSubmit={async (event, questions) => {
          try {
            event.preventDefault();

            setIsLoading(true);
            let barcode = questions["barcode"].value;
            let firstname = questions["firstname"].value;
            let lastname = questions["lastname"].value;
            const dobISO = questions["dob"].value;
            const dobDate = new Date(dobISO || "");
            const dob = isValid(dobDate)
              ? format(dobDate, "MM/dd/yyyy")
              : undefined;

            const registrationID = "TEST";

            if (!barcode || !firstname || !dob || !lastname) {
              confirmAlert({
                message: fbt(
                  "Barcode, first name, last name and date of birth are required.",
                  "registration: alert when information is missing."
                ),
                theme,
              });
              return;
            }
            barcode = barcode.trim();
            firstname = firstname.trim();
            lastname = lastname.trim();

            // check registration
            const {
              result: registrationResult,
              registrationToken = "",
            } = await firebaseApp.checkRegistration(
              registrationID,
              barcode,
              "TEST", // TODO(ram): replace with orgId
              testRunUID,
              auth.uid
            );

            setIsLoading(false);

            switch (registrationResult) {
              case RegistrationResult.VALID:
                confirmAlert({
                  message: fbt(
                    "The kit for " +
                      fbt.param("firstname", firstname) +
                      " was successfully registered",
                    "registration: alert to confirm registration"
                  ),
                  theme,
                });
                break;
              case RegistrationResult.ALREADY_ACTIVATED:
                // USC-1297 disable this during usability study
                /* confirmAlert({
                  title: "This barcode has already been used.",
                  message: "Please use a different barcode.",
                  theme,
                }); 
                return;
              */
                confirmAlert({
                  message: fbt(
                    "The kit for " +
                      fbt.param("firstname", firstname) +
                      " was successfully registered",
                    "registration: alert to confirm registration"
                  ),
                  theme,
                });
                break;
              case RegistrationResult.BARCODE_NOT_FOUND:
                confirmAlert({
                  title: fbt("Sorry", "registration: alert title"),
                  message: fbt(
                    fbt.param("barcode", barcode) +
                      " doesn’t look like a barcode we know. Please try again.",
                    "registration: alert"
                  ),
                  theme,
                });
                return;
              default:
                confirmAlert({
                  title: fbt(
                    "Invalid registration details",
                    "registation: alert title"
                  ),
                  message: fbt(
                    "The information you entered do not match our records. Please try again.",
                    "registration: alert"
                  ),
                  theme,
                });
                return;
            }

            // USC-1297 disable this during usability study
            //await firebaseApp.redeemRegistration(barcode, "TEST"); // TODO: USC-1206 - integrate orgID better

            await dispatch(
              setNewTestrunProfile({
                firstname,
                lastname,
                dob,
              })
            );

            await dispatch(
              updateRegistration({ barcode, registrationID, registrationToken })
            );

            // Resets the state.
            stepReadyCallback(false);
            const nextStep = getNextDefaultStep({
              currentStepName: step!,
            });
            const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
              params: {
                ":testRunUID": testRunUID,
                ":step": nextStep!,
              },
            });
            history.push(path);
          } catch (e) {
            confirmAlert({
              title: fbt(
                "oops",
                "registration: alert title for unhandled error"
              ),
              message: e.message,
              theme,
            });
          }
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={5} md={4}>
            <QuestionnaireInput
              formatter={(text: string) => text.trim().toUpperCase()}
              label={fbt("Enter kit barcode", "registration: field label")}
              mandatory={true}
              name="barcode"
              type="text"
              value={storedBarcode}
              endAdornment={photoCaptureLink}
            />
          </Grid>
        </Grid>
        <Paragraph>
          <fbt desc="registration: field requirements">
            Enter the name and date of birth for the person being tested.
          </fbt>
        </Paragraph>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5} md={4}>
            <QuestionnaireInput
              formatter={capitalizeWords}
              label={fbt("First name", "registration: field label")}
              mandatory={true}
              name="firstname"
              type="text"
              value={""}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <QuestionnaireInput
              formatter={capitalizeWords}
              label={fbt("Last name", "registration: field label")}
              mandatory={true}
              name="lastname"
              type="text"
              value={""}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5} md={4}>
            <QuestionnaireInput
              label={fbt(
                "Date of birth (MM/DD/YYYY)",
                "registration: field label"
              )}
              mandatory={true}
              name="dob"
              value={""}
              type="date"
              minDate={new Date("1900-01-01T00:00:00")}
              maxDate={new Date()}
              disableFuture={true}
              validators={[questionnaireDateValidator]}
            />
          </Grid>
        </Grid>
        <Paragraph>
          <fbt desc="registration: note">
            Please note: The name and date of birth information must match the
            original kit order.
          </fbt>
        </Paragraph>
      </QuestionnaireFormMemoized>
    </div>
  );
});

export default RegistrationPanel;
